import moment from "moment";
import type { AnalyticResponse, QuickDates } from "./types";
import type { CountingUnit } from "./constants";
import { ApplicationStatus } from "shared/types/application";
import type {
  PipelineStage,
  PipelineTransition
} from "./components/PipelineCard";

export type StageTransitionSplit = {
  stillInStage: PipelineTransition | undefined;
  movements: PipelineTransition[];
  base: number;
};

export const splitStageTransitions = (
  stage: PipelineStage
): StageTransitionSplit => {
  const stillInStage = stage.transitions.find(
    (t) => t.stage_id === stage.stage_id
  );
  const movements = stage.transitions.filter(
    (t) => t.stage_id !== stage.stage_id
  );
  // base falls back to 1 when total_count is 0 to avoid divide-by-zero;
  // callers should already guard the empty case (no transitions to render).
  return { stillInStage, movements, base: stage.total_count || 1 };
};

export const generateGraphData = (
  analytics?: AnalyticResponse["status_count_details"]
) => {
  // If analytics is not provided, return an empty graph with only the "applications" node
  if (!analytics)
    return {
      nodes: [{ id: "applications" }],
      links: []
    };

  // Extract statuses from analytics data where the count is greater than 0
  const statusKeys = Object.keys(analytics).filter(
    (key) => analytics[key as ApplicationStatus] > 0
  );

  // Generate nodes based on available statuses
  const nodes = [
    { id: "applications" },
    ...statusKeys.map((status) => ({ id: status }))
  ];

  // Create links based on transitions from "applications" (root node) to other statuses
  const links = statusKeys.map((status) => ({
    source: "applications",
    target: status,
    value: analytics[status as ApplicationStatus]
  }));

  return { nodes, links };
};

export const getApplicationsQuickDates = (partnerCreatedAt?: string) => ({
  "This month": {
    start_date: moment().startOf("month").toDate(),
    end_date: moment().endOf("month").startOf("day").toDate()
  },
  "Previous month": {
    start_date: moment()
      .subtract(1, "month")
      .startOf("month")
      .startOf("day")
      .toDate(),
    end_date: moment()
      .subtract(1, "month")
      .endOf("month")
      .startOf("day")
      .toDate()
  },
  "This week": {
    start_date: moment().startOf("week").startOf("day").toDate(),
    end_date: moment().endOf("week").startOf("day").toDate()
  },
  "Last week": {
    start_date: moment()
      .subtract(1, "week")
      .startOf("week")
      .startOf("day")
      .toDate(),
    end_date: moment().subtract(1, "week").endOf("week").startOf("day").toDate()
  },
  "Year to date": {
    start_date: moment().startOf("year").startOf("day").toDate(),
    end_date: moment().startOf("day").toDate()
  },
  "All time": {
    start_date: moment(partnerCreatedAt).startOf("day").toDate(),
    end_date: moment().startOf("day").toDate()
  }
});

export const getCrmQuickDates = (partnerCreatedAt?: string) => ({
  "Last 7 days": {
    start_date: moment().subtract(6, "days").startOf("day").toDate(),
    end_date: moment().startOf("day").toDate()
  },
  "Last 30 days": {
    start_date: moment().subtract(29, "days").startOf("day").toDate(),
    end_date: moment().startOf("day").toDate()
  },
  "Last 60 days": {
    start_date: moment().subtract(59, "days").startOf("day").toDate(),
    end_date: moment().startOf("day").toDate()
  },
  "Last 90 days": {
    start_date: moment().subtract(89, "days").startOf("day").toDate(),
    end_date: moment().startOf("day").toDate()
  },
  "Last 12 months": {
    start_date: moment().subtract(12, "months").startOf("day").toDate(),
    end_date: moment().startOf("day").toDate()
  },
  "All time": {
    start_date: moment(partnerCreatedAt).startOf("day").toDate(),
    end_date: moment().startOf("day").toDate()
  }
});

export const noOffsetDate = (date: moment.MomentInput) =>
  moment(date).startOf("day").format("YYYY-MM-DD");

export const getDateRangeLabel = (
  quickDates: QuickDates,
  startDate: Date,
  endDate: Date
): string => {
  const match = Object.entries(quickDates).find(
    ([, value]) =>
      noOffsetDate(value.start_date) === noOffsetDate(startDate) &&
      noOffsetDate(value.end_date) === noOffsetDate(endDate)
  );
  if (match) return match[0];
  const start = moment(startDate).format("MMM D, YYYY");
  const end = moment(endDate).format("MMM D, YYYY");
  if (start === end) return start;
  return `${start} - ${end}`;
};

const parseContentDispositionFilename = (
  header: string | null | undefined
): string | null => {
  if (!header) return null;
  const match = header.match(/filename\*?=(?:UTF-8'')?["']?([^"';\n]+)["']?/i);
  return match ? decodeURIComponent(match[1].trim()) : null;
};

export function downloadCsvFile(response) {
  const csvData = response.data;
  const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });

  const contentDisposition =
    response.headers?.["content-disposition"] ??
    response.headers?.get?.("content-disposition");
  const filename =
    parseContentDispositionFilename(contentDisposition) ?? "export.csv";

  // eslint-disable-next-line compat/compat -- only unsupported in Opera Mini which we don't target
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);

  document.body.appendChild(link);

  link.click();

  document.body.removeChild(link);
  // eslint-disable-next-line compat/compat -- only unsupported in Opera Mini which we don't target
  URL.revokeObjectURL(url);
}

// What the POST /analytics endpoints accept as `filters`. Properties go over the wire
// as bare ids.
export type AnalyticsRequestFilters = {
  cities?: string[];
  property_ids?: number[];
  // Verification template ids are strings on this API; application statuses are
  // ApplicationStatus values, kept loose here because the filter bar hands them
  // over as strings (PLT-634).
  template_ids?: string[];
  property_group_ids?: string[];
  statuses?: string[];
  states?: string[];
  start_date?: string;
  end_date?: string;
  // Omitted for the group default so the request stays byte-identical to what it was before the
  // toggle existed. The API rejects a value it does not know, so only send one of its own.
  counting_unit?: CountingUnit;
};

// Empty arrays are dropped so the API reads them as "no filter" instead of "match nothing".
export const normalizeFilters = (flts: AnalyticsRequestFilters) => {
  const normalized: AnalyticsRequestFilters = { ...flts };

  (Object.keys(normalized) as (keyof AnalyticsRequestFilters)[]).forEach(
    (key) => {
      const val = normalized[key];
      if (Array.isArray(val) && !val.length) delete normalized[key];
    }
  );

  return {
    ...normalized,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  };
};

// Graph subtitles name the range they cover, so the reader never has to look
// back up at the filter to know what the chart is showing.
export const withDateLabel = (
  subtitle?: string,
  dateLabel?: string
): string | undefined =>
  subtitle && dateLabel ? `${subtitle} – ${dateLabel}` : subtitle;

// Weekly buckets are keyed by their start date, so a label spans start through
// start + 6 days. The month is repeated only when the week crosses into the next
// one, which keeps the label narrow enough to fit several weeks on an axis.
//
// The year is left off until the caller asks for it. A week straddling New Year
// needs both years to be unambiguous; any other week takes one trailing year.
export const formatWeekRange = (
  raw: string,
  { showYear = false }: { showYear?: boolean } = {}
): string => {
  const start = moment(raw, moment.ISO_8601, true);
  if (!start.isValid()) return raw;

  const end = start.clone().add(6, "days");
  const endFormat = start.isSame(end, "month") ? "D" : "MMM D";

  if (!showYear) return `${start.format("MMM D")} - ${end.format(endFormat)}`;
  if (!start.isSame(end, "year"))
    return `${start.format("MMM D, YY")} - ${end.format("MMM D, YY")}`;

  return `${start.format("MMM D")} - ${end.format(endFormat)}, ${end.format("YY")}`;
};

// Week labels stay short until the chart covers more than one calendar year, where
// a bare "Dec 29 - Jan 4" no longer says which year it belongs to.
export const weekRangesSpanYears = (categories: string[]): boolean => {
  const years = new Set<number>();

  categories.forEach((category) => {
    const start = moment(category, moment.ISO_8601, true);
    if (!start.isValid()) return;
    years.add(start.year());
    years.add(start.clone().add(6, "days").year());
  });

  return years.size > 1;
};

// Week ranges are roughly twice as wide as a single date, so an axis fits about
// half as many of them.
// Roughly how wide a 12px tick label runs, per character. Only needs to be close
// enough to decide how many labels fit before they touch.
const TICK_CHAR_PX = 6.6;
// Breathing room between neighbouring labels.
const TICK_GUTTER_PX = 16;

// Widest tick label in the set, in pixels — the gap two neighbouring labels need
// between them, since each is centred on its own point.
export const getTickLabelWidth = (labels: string[]): number => {
  const longest = labels.reduce((max, label) => Math.max(max, label.length), 0);
  return longest * TICK_CHAR_PX + TICK_GUTTER_PX;
};

// How far the plot has to sit in from an edge for that edge's label to stay fully
// inside the card. Measured from the edge label itself rather than the widest in
// the set, so neither end reserves more room than it needs. Half the gutter is
// kept as slack, since the per-character width is only an estimate and a label
// that renders wider than predicted would clip.
export const getEdgeLabelInset = (label: string | undefined): number =>
  label ? Math.ceil((label.length * TICK_CHAR_PX) / 2) + TICK_GUTTER_PX / 2 : 0;

// Picks x-axis labels that are guaranteed not to touch at the width the chart is
// actually rendered at, rather than at a width assumed from the breakpoint. Pass
// the plot width (the box the points span), not the whole chart.
//
// The stride comes from the pixel budget: every gap is a whole number of strides
// wide, so the tightest gap on the axis is known up front instead of being
// whatever the remainder leaves behind. `maxTicks` stays the design ceiling for a
// comfortably wide chart.
export const getFittingTickValues = ({
  labels,
  width,
  maxTicks
}: {
  labels: string[];
  width: number;
  maxTicks: number;
}): string[] => {
  const last = labels.length - 1;
  if (last <= 0) return labels;
  // Before the first measurement, fall back to the breakpoint ceiling.
  if (!width) return getXTickValues(labels, maxTicks);

  // Every label is centred on its point, so neighbours each need half a label of
  // room — one full label of gap between adjacent ticks.
  const perTick = getTickLabelWidth(labels);

  const pxPerIndex = width / last;
  const strideForWidth = Math.ceil(perTick / pxPerIndex);
  const strideForCeiling =
    maxTicks > 1 ? Math.ceil(last / (maxTicks - 1)) : last;
  const stride = Math.max(1, strideForWidth, strideForCeiling);

  if (stride >= last) return [labels[0], labels[last]];

  const indices: number[] = [];
  for (let i = 0; i <= last; i += stride) indices.push(i);

  // Dropping the tick that sits closer than a stride to the end keeps the final
  // gap at least as wide as the others, so the right-hand labels never bunch.
  if (indices[indices.length - 1] !== last) {
    while (indices.length > 1 && last - indices[indices.length - 1] < stride)
      indices.pop();
    indices.push(last);
  }

  return indices.map((i) => labels[i]);
};

export const getDateTickCount = ({
  weeklyRange,
  isMobile
}: {
  weeklyRange?: boolean;
  isMobile: boolean;
}): number => {
  if (weeklyRange) return isMobile ? 3 : 8;
  return isMobile ? 5 : 11;
};

export const formatCompactNumber = (num: number): string => {
  if (num >= 1e6) return (num / 1e6).toFixed(1).replace(/\.0$/, "") + "m";
  if (num >= 1e3) return (num / 1e3).toFixed(1).replace(/\.0$/, "") + "k";
  return String(num);
};

export type DurationUnit = "minutes" | "hours";

// The dashboard API serves some durations in seconds and others in minutes.
// Normalizing on the way in keeps every display helper below on one input unit.
export type ApiDurationUnit = "seconds" | "minutes";

const SECONDS_PER_MINUTE = 60;

export const toMinutes = (value: number, apiUnit: ApiDurationUnit): number =>
  apiUnit === "seconds" ? value / SECONDS_PER_MINUTE : value;

export const toDurationDisplay = (
  minutes: number,
  unit: DurationUnit
): number => (unit === "hours" ? minutes / 60 : minutes);

export const formatDurationValue = (
  value: number,
  unit: DurationUnit
): string =>
  unit === "hours"
    ? `${(Math.round(value * 10) / 10).toLocaleString("en-US")}h`
    : `${Math.round(value).toLocaleString("en-US")}m`;

const NICE_STEPS = [1, 2, 3, 4, 5, 8, 10];
const TICK_RANGE = [4, 5, 6];

const niceStep = (raw: number): number => {
  if (raw <= 0) return 1;
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
  const residual = raw / magnitude;
  return (NICE_STEPS.find((s) => residual <= s) ?? 10) * magnitude;
};

type NiceAxis = { axisMax: number; tickCount: number };

export const getNiceAxis = (max: number): NiceAxis => {
  if (max <= 0) return { axisMax: 5, tickCount: 5 };
  if (max <= 5) return { axisMax: max, tickCount: max };

  let best: NiceAxis = { axisMax: Infinity, tickCount: 5 };
  TICK_RANGE.forEach((count) => {
    const step = niceStep(max / count);
    const candidate = step * count;
    if (candidate >= max && candidate < best.axisMax)
      best = { axisMax: candidate, tickCount: count };
  });
  return best;
};

export const getTicks = (axisMax: number, tickCount: number): number[] => {
  const step = axisMax / tickCount;
  return Array.from({ length: tickCount + 1 }, (_, i) => Math.round(i * step));
};

// Blank x-slots that center a lone category, which a point scale would
// otherwise pin to the left edge. Zero-width spaces keep the two keys distinct
// and invisible if they ever reach the axis.
export const EDGE_PAD_LEFT = "\u200B";
export const EDGE_PAD_RIGHT = "\u200B\u200B";

export const getXTickValues = (labels: string[], max: number): string[] => {
  if (labels.length <= max) return labels;
  const step = Math.ceil(labels.length / max);
  const indices: number[] = [];
  for (let i = 0; i < labels.length; i += step) indices.push(i);

  // The last label always shows, but the evenly spaced tick before it can land
  // right next to it and the two collide. Drop that one when it's closer than
  // half a step.
  //
  // Charts that need a guaranteed minimum gap use getFittingTickValues instead;
  // this stays as it was for the callers that pick a tick count by breakpoint.
  const last = labels.length - 1;
  if (indices[indices.length - 1] !== last) {
    if (last - indices[indices.length - 1] < step / 2) indices.pop();
    indices.push(last);
  }
  return indices.map((i) => labels[i]);
};

type PropertyKeyed = {
  property_id?: number | null;
  property_name?: string | null;
};

export type PropertyGroup<T> = {
  key: string;
  name: string;
  items: T[];
};

export const groupByProperty = <T>(
  items: T[],
  selector: (item: T) => {
    id: number | null | undefined;
    name: string | null | undefined;
  }
): PropertyGroup<T>[] => {
  const map = new Map<string, PropertyGroup<T>>();
  items.forEach((item) => {
    const { id, name } = selector(item);
    const key = id != null ? `id:${id}` : `name:${name ?? "__unknown"}`;
    const existing = map.get(key);
    if (existing) existing.items.push(item);
    else map.set(key, { key, name: name ?? "—", items: [item] });
  });
  return Array.from(map.values());
};

export const propertyKeyedSelector = (item: PropertyKeyed) => ({
  id: item.property_id ?? null,
  name: item.property_name
});

export const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return "--";
  const m = moment(iso);
  return m.isValid() ? m.format("MMM D, YYYY [at] h:mm a") : "--";
};

export const formatDuration = (
  seconds: number | null | undefined
): string | null => {
  if (seconds == null) return null;
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${String(secs).padStart(2, "0")} min`;
};

// Fixed AI response time (seconds) shown for conversations whose lead was created
// automatically during the call — the backend leaves team_response_time_secs null
// for these, and the client surfaces this constant instead.
export const AI_RESPONSE_TIME_SECONDS = 2;

// Compound, leading-zero-suppressed duration for the response-time metric. Spans a
// couple of seconds (AI) to several days (slow team follow-up). Accepts a float
// (the API average) or an integer (a per-row value).
export const formatResponseTime = (
  seconds: number | null | undefined
): string | null => {
  if (seconds == null) return null;
  const total = Math.round(seconds);

  if (total < 60) return `${total} Sec`;

  if (total < 3600) {
    const m = Math.floor(total / 60);
    const s = total % 60;
    return s ? `${m}m ${s}sec` : `${m}m`;
  }

  if (total < 86400) {
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    return m ? `${h}h ${m}m` : `${h}h`;
  }

  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  return [`${d}d`, h ? `${h}h` : null, m ? `${m}min` : null]
    .filter(Boolean)
    .join(" ");
};

// Per-conversation response time for the table/drawer. A stored value is the team
// follow-up time; a null value with a lead attached means the lead was created by
// the AI during the call, so we show the fixed AI time; otherwise nothing has
// happened yet (null → callers render "--").
export const rowResponseTime = (row: {
  team_response_time_secs: number | null;
  lead_id: number | null;
}): string | null => {
  if (row.team_response_time_secs != null)
    return formatResponseTime(row.team_response_time_secs);
  if (row.lead_id != null) return formatResponseTime(AI_RESPONSE_TIME_SECONDS);
  return null;
};

// Caller numbers are stored digits-only and may carry a leading US country code;
// the create-lead form expects a bare 10-digit number.
export const normalizeUsPhone = (
  raw: string | null | undefined
): string | undefined => {
  if (!raw) return undefined;
  const digits = raw.replace(/\D/g, "");
  const local =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  return local.length === 10 ? local : undefined;
};
